import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { toast } from 'sonner'
import { format, parseISO, addDays } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { 
  Target, Plus, Check, Clock, ChevronRight, Bell, BellOff, 
  Play, Square, Pencil, Trash2, X, Moon, Sun, Loader2,
  Sparkles, TrendingUp, Award, Calendar, Settings, ChevronDown
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from '../components/ui/drawer'
import { Badge } from '../components/ui/badge'

const API = process.env.REACT_APP_BACKEND_URL + '/api'

export default function KaizenPlanPage() {
  const navigate = useNavigate()
  const { t, locale } = useLanguage()
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  // State
  const [loading, setLoading] = useState(true)
  const [todayPlan, setTodayPlan] = useState(null)
  const [categories, setCategories] = useState([])
  const [stats, setStats] = useState(null)
  const [notificationSettings, setNotificationSettings] = useState(null)
  
  // Form state
  const [showPlanForm, setShowPlanForm] = useState(false)
  const [planDate, setPlanDate] = useState('today') // 'today' or 'tomorrow'
  const [planningMode, setPlanningMode] = useState('morning')
  // Now each improvement has its own category
  const [improvements, setImprovements] = useState([{ 
    category_id: '', 
    title: '', 
    description: '', 
    estimated_minutes: '', 
    commitment_time: '', 
    minimum_version: '' 
  }])
  const [saving, setSaving] = useState(false)
  
  // Commitment modal
  const [showCommitment, setShowCommitment] = useState(false)
  
  // Close day modal
  const [showCloseDay, setShowCloseDay] = useState(false)
  const [closeDayForm, setCloseDayForm] = useState({
    reflection_note: '',
    next_step_for_tomorrow: '',
    emotional_state: ''
  })
  
  // Notification settings modal
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)
  
  // Fetch data
  const fetchTodayPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kaizen/plans/today`)
      setTodayPlan(response.data)
    } catch (error) {
      console.error('Error fetching today plan:', error)
    }
  }, [])
  
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`)
      setCategories(response.data.filter(c => c.is_active))
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }, [])
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kaizen/stats`)
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [])
  
  const fetchNotificationSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kaizen/notification-settings`)
      setNotificationSettings(response.data)
    } catch (error) {
      console.error('Error fetching notification settings:', error)
    }
  }, [])
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([
        fetchTodayPlan(),
        fetchCategories(),
        fetchStats(),
        fetchNotificationSettings()
      ])
      setLoading(false)
    }
    loadData()
  }, [fetchTodayPlan, fetchCategories, fetchStats, fetchNotificationSettings])
  
  // Toggle notifications
  const toggleNotifications = async () => {
    const newEnabled = !notificationSettings?.kaizen_push_enabled
    
    // If enabling, check push permission
    if (newEnabled && 'Notification' in window) {
      if (Notification.permission === 'denied') {
        toast.error(locale === 'es-MX' 
          ? 'Notificaciones bloqueadas. Habilítalas desde la configuración del dispositivo.'
          : 'Notifications blocked. Enable them from device settings.')
        return
      }
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
          toast.error(locale === 'es-MX'
            ? 'No pudimos activar notificaciones.'
            : 'Could not enable notifications.')
          return
        }
      }
    }
    
    try {
      const response = await axios.put(`${API}/kaizen/notification-settings`, {
        kaizen_push_enabled: newEnabled
      })
      setNotificationSettings(response.data)
      toast.success(newEnabled ? t('kaizen.notificationsEnabled') : t('kaizen.notificationsDisabled'))
    } catch (error) {
      toast.error(t('common.error'))
    }
  }
  
  // Add improvement
  const addImprovement = () => {
    if (improvements.length >= 3) {
      toast.error(t('kaizen.maxImprovementsReached'))
      return
    }
    setImprovements([...improvements, { 
      category_id: '', 
      title: '', 
      description: '', 
      estimated_minutes: '', 
      commitment_time: '', 
      minimum_version: '' 
    }])
  }
  
  // Remove improvement
  const removeImprovement = (index) => {
    if (improvements.length === 1) return
    setImprovements(improvements.filter((_, i) => i !== index))
  }
  
  // Update improvement
  const updateImprovement = (index, field, value) => {
    const updated = [...improvements]
    updated[index][field] = value
    setImprovements(updated)
  }
  
  // Get date string for plan
  const getDateString = () => {
    const today = new Date()
    if (planDate === 'tomorrow') {
      return format(addDays(today, 1), 'yyyy-MM-dd')
    }
    return format(today, 'yyyy-MM-dd')
  }
  
  // Save plan
  const savePlan = async () => {
    // Validate - each improvement needs title and category
    const validImprovements = improvements.filter(i => i.title.trim() && i.category_id)
    if (validImprovements.length === 0) {
      toast.error(t('kaizen.minOneImprovement'))
      return
    }
    
    // Check if all improvements have a category
    const missingCategory = improvements.find(i => i.title.trim() && !i.category_id)
    if (missingCategory) {
      toast.error(locale === 'es-MX' ? 'Selecciona una categoría para cada mejora' : 'Select a category for each improvement')
      return
    }
    
    setSaving(true)
    try {
      const payload = {
        date: getDateString(),
        planning_mode: planningMode,
        // Use first improvement's category as main (for backwards compatibility)
        category_id: validImprovements[0].category_id,
        subcategory_id: null,
        items: validImprovements.map(i => ({
          title: i.title.trim(),
          description: i.description?.trim() || null,
          estimated_minutes: i.estimated_minutes ? parseInt(i.estimated_minutes) : null,
          commitment_time: i.commitment_time || null,
          minimum_version: i.minimum_version?.trim() || null,
          category_id: i.category_id // Include category per item
        }))
      }
      
      const response = await axios.post(`${API}/kaizen/plans`, payload)
      setTodayPlan(response.data)
      toast.success(t('kaizen.planSaved'))
      setShowPlanForm(false)
      setShowCommitment(true) // Show commitment modal
      fetchStats()
    } catch (error) {
      const msg = error.response?.data?.detail || t('kaizen.errorSaving')
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }
  
  // Confirm commitment
  const confirmCommitment = async () => {
    if (!todayPlan) return
    
    try {
      await axios.post(`${API}/kaizen/plans/${todayPlan.id}/confirm`, { send_push: true })
      toast.success(t('kaizen.commitmentConfirmed'))
      setShowCommitment(false)
      fetchTodayPlan()
    } catch (error) {
      toast.error(t('common.error'))
    }
  }
  
  // Update item status
  const updateItemStatus = async (itemId, newStatus) => {
    if (!todayPlan) return
    
    try {
      await axios.put(`${API}/kaizen/plans/${todayPlan.id}/items/${itemId}`, {
        status: newStatus
      })
      fetchTodayPlan()
      fetchStats()
    } catch (error) {
      toast.error(t('common.error'))
    }
  }
  
  // Close day
  const handleCloseDay = async () => {
    if (!todayPlan) return
    
    try {
      await axios.post(`${API}/kaizen/plans/${todayPlan.id}/close`, closeDayForm)
      toast.success(t('kaizen.dayClosed'))
      setShowCloseDay(false)
      fetchTodayPlan()
      fetchStats()
    } catch (error) {
      toast.error(t('common.error'))
    }
  }
  
  // Save notification settings
  const saveNotificationSettings = async () => {
    try {
      const response = await axios.put(`${API}/kaizen/notification-settings`, notificationSettings)
      setNotificationSettings(response.data)
      toast.success(t('common.saved'))
      setShowNotificationSettings(false)
    } catch (error) {
      toast.error(t('common.error'))
    }
  }
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-emerald-500 text-white'
      case 'in_progress': return 'bg-blue-500 text-white'
      case 'partial': return 'bg-yellow-500 text-white'
      case 'skipped': return 'bg-gray-400 text-white'
      default: return 'bg-secondary text-foreground'
    }
  }
  
  // Calculate progress
  const getProgress = () => {
    if (!todayPlan?.items?.length) return { done: 0, total: 0 }
    const done = todayPlan.items.filter(i => i.status === 'done' || i.status === 'partial').length
    return { done, total: todayPlan.items.length }
  }
  
  // Open plan form
  const openPlanForm = () => {
    // Determine if planning for today or tomorrow based on time
    const hour = new Date().getHours()
    if (hour >= 20) {
      setPlanDate('tomorrow')
      setPlanningMode('night_before')
    } else {
      setPlanDate('today')
      setPlanningMode('morning')
    }
    setImprovements([{ 
      category_id: '', 
      title: '', 
      description: '', 
      estimated_minutes: '', 
      commitment_time: '', 
      minimum_version: '' 
    }])
    setShowPlanForm(true)
  }
  
  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId)
    return cat?.name || ''
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }
  
  const progress = getProgress()
  
  return (
    <div className="p-4 md:p-8 pb-32 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6 text-primary" />
            {t('kaizen.title')}
          </h1>
          <p className="text-muted-foreground text-sm">{t('kaizen.subtitle')}</p>
        </div>
        
        {/* Notification bell */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={toggleNotifications}
          className="relative"
          data-testid="kaizen-notification-toggle"
        >
          {notificationSettings?.kaizen_push_enabled ? (
            <Bell className="w-5 h-5 text-primary" />
          ) : (
            <BellOff className="w-5 h-5 text-muted-foreground" />
          )}
        </Button>
      </div>
      
      {/* Motivational quote */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4 mb-6 border border-primary/20">
        <p className="text-sm italic text-center">
          <Sparkles className="w-4 h-4 inline mr-1 text-primary" />
          {t('kaizen.smallProgress')}
        </p>
      </div>
      
      {/* Today's Plan Card */}
      {todayPlan ? (
        <Card className="mb-6 border-primary/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {t('kaizen.planToday')}
              </CardTitle>
              <Badge className={getStatusColor(todayPlan.status_summary)}>
                {progress.done}/{progress.total}
              </Badge>
            </div>
            <CardDescription className="flex items-center gap-2 flex-wrap">
              {/* Show all unique categories from items */}
              {(() => {
                const uniqueCategories = [...new Set(todayPlan.items?.map(i => i.category_name).filter(Boolean))]
                if (uniqueCategories.length === 0 && todayPlan.category_name) {
                  return <span className="font-medium text-foreground">{todayPlan.category_name}</span>
                }
                return uniqueCategories.map((catName, idx) => (
                  <span key={catName}>
                    <span className="font-medium text-foreground">{catName}</span>
                    {idx < uniqueCategories.length - 1 && <span className="text-muted-foreground mx-1">•</span>}
                  </span>
                ))
              })()}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {/* Items list */}
            {todayPlan.items?.map((item, idx) => (
              <div 
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  item.status === 'done' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                  item.status === 'in_progress' ? 'bg-blue-500/10 border border-blue-500/30' :
                  item.status === 'partial' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  item.status === 'skipped' ? 'bg-gray-500/10 border border-gray-500/30 opacity-60' :
                  'bg-secondary/50 border border-transparent'
                }`}
                data-testid={`kaizen-item-${item.id}`}
              >
                <span className="text-lg font-bold text-muted-foreground w-6">{idx + 1}</span>
                
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${item.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                    {item.title}
                  </p>
                  {/* Show category for each item */}
                  {item.category_name && (
                    <p className="text-xs text-primary">{item.category_name}</p>
                  )}
                  {item.minimum_version && (
                    <p className="text-xs text-muted-foreground">Min: {item.minimum_version}</p>
                  )}
                </div>
                
                {/* Quick actions */}
                <div className="flex items-center gap-1">
                  {item.status === 'pending' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-blue-500"
                      onClick={() => updateItemStatus(item.id, 'in_progress')}
                      data-testid={`start-item-${item.id}`}
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {(item.status === 'pending' || item.status === 'in_progress') && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-emerald-500"
                      onClick={() => updateItemStatus(item.id, 'done')}
                      data-testid={`complete-item-${item.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Actions */}
            <div className="flex gap-2 pt-3">
              {!todayPlan.commitment_confirmed && (
                <Button 
                  className="flex-1 rounded-full"
                  onClick={() => setShowCommitment(true)}
                  data-testid="show-commitment-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t('kaizen.confirmCommitment')}
                </Button>
              )}
              {!todayPlan.day_closed_at && todayPlan.commitment_confirmed && (
                <Button 
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setShowCloseDay(true)}
                  data-testid="close-day-btn"
                >
                  <Moon className="w-4 h-4 mr-2" />
                  {t('kaizen.closeDay')}
                </Button>
              )}
            </div>
            
            {todayPlan.day_closed_at && (
              <div className="text-center py-2 text-emerald-600 text-sm font-medium">
                <Check className="w-4 h-4 inline mr-1" />
                {t('kaizen.dayClosed')}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* No plan yet */
        <Card className="mb-6 border-dashed border-2">
          <CardContent className="py-8 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-semibold mb-2">{t('kaizen.noPlanYet')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('kaizen.maxImprovements')}</p>
            <Button onClick={openPlanForm} className="rounded-full" data-testid="create-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('kaizen.createPlan')}
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Stats */}
      {stats && stats.total_plans > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('kaizen.stats')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{stats.current_streak}</p>
                <p className="text-xs text-muted-foreground">{t('kaizen.currentStreak')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.best_streak}</p>
                <p className="text-xs text-muted-foreground">{t('kaizen.bestStreak')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{stats.completion_rate}%</p>
                <p className="text-xs text-muted-foreground">{t('kaizen.completionRate')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.total_plans}</p>
                <p className="text-xs text-muted-foreground">{t('kaizen.totalPlans')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Notification Settings Button */}
      {notificationSettings?.kaizen_push_enabled && (
        <Button
          variant="outline"
          className="w-full rounded-full"
          onClick={() => setShowNotificationSettings(true)}
          data-testid="notification-settings-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          {t('kaizen.notificationSettings')}
        </Button>
      )}
      
      {/* Plan Form Modal */}
      <Drawer open={showPlanForm} onOpenChange={setShowPlanForm}>
        <DrawerContent className="max-h-[85vh] h-[85vh] flex flex-col">
          <DrawerHeader className="flex-shrink-0">
            <DrawerTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              {t('kaizen.createPlan')}
            </DrawerTitle>
            <DrawerDescription>
              {t('kaizen.planFor')} {planDate === 'today' ? t('kaizen.today') : t('kaizen.tomorrow')}
            </DrawerDescription>
          </DrawerHeader>
          
          {/* Scrollable content area */}
          <div 
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-safe touch-pan-y"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-4 py-4 pb-8">
              {/* Planning mode */}
              <div className="flex gap-2">
                <Button
                  variant={planningMode === 'night_before' ? 'default' : 'outline'}
                  className="flex-1 rounded-full"
                  onClick={() => { setPlanningMode('night_before'); setPlanDate('tomorrow') }}
                >
                  <Moon className="w-4 h-4 mr-2" />
                  {t('kaizen.nightBefore')}
                </Button>
                <Button
                  variant={planningMode === 'morning' ? 'default' : 'outline'}
                  className="flex-1 rounded-full"
                  onClick={() => { setPlanningMode('morning'); setPlanDate('today') }}
                >
                  <Sun className="w-4 h-4 mr-2" />
                  {t('kaizen.morning')}
                </Button>
              </div>
              
              {/* Improvements - each with its own category */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{locale === 'es-MX' ? 'Mejoras (1-3 categorías)' : 'Improvements (1-3 categories)'}</Label>
                  <span className="text-xs text-muted-foreground">{improvements.length}/3</span>
                </div>
                
                {improvements.map((imp, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-secondary/50 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-primary text-lg">{idx + 1}</span>
                      <div className="flex-1 space-y-2">
                        {/* Category selector for this improvement */}
                        <Select 
                          value={imp.category_id} 
                          onValueChange={(val) => updateImprovement(idx, 'category_id', val)}
                        >
                          <SelectTrigger data-testid={`category-select-${idx}`}>
                            <SelectValue placeholder={locale === 'es-MX' ? 'Selecciona categoría' : 'Select category'} />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Input
                          placeholder={t('kaizen.improvementTitlePlaceholder')}
                          value={imp.title}
                          onChange={(e) => updateImprovement(idx, 'title', e.target.value)}
                          data-testid={`improvement-title-${idx}`}
                        />
                      </div>
                      {improvements.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive flex-shrink-0"
                          onClick={() => removeImprovement(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {locale === 'es-MX' ? 'Versión mínima' : 'Minimum version'}
                        </Label>
                        <Input
                          placeholder={t('kaizen.minimumVersionPlaceholder')}
                          value={imp.minimum_version}
                          onChange={(e) => updateImprovement(idx, 'minimum_version', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {locale === 'es-MX' ? 'Hora compromiso' : 'Commitment time'}
                        </Label>
                        <Input
                          type="time"
                          value={imp.commitment_time}
                          onChange={(e) => updateImprovement(idx, 'commitment_time', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {improvements.length < 3 && (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={addImprovement}
                    data-testid="add-improvement-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {locale === 'es-MX' ? 'Agregar otra categoría' : 'Add another category'}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Fixed footer with save button */}
          <div className="flex-shrink-0 p-4 border-t bg-background">
            <Button
              className="w-full rounded-full"
              onClick={savePlan}
              disabled={saving}
              data-testid="save-plan-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {t('kaizen.savePlan')}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
      
      {/* Commitment Modal */}
      <Dialog open={showCommitment} onOpenChange={setShowCommitment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              {t('kaizen.commitment')}
            </DialogTitle>
          </DialogHeader>
          
          {todayPlan && (
            <div className="py-4">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 border border-primary/20">
                <p className="text-sm mb-3">
                  {locale === 'es-MX' 
                    ? 'Me comprometo a trabajar hoy en estas mejoras:' 
                    : 'I commit to working on these improvements today:'}
                </p>
                <ol className="list-decimal list-inside space-y-2 mb-3">
                  {todayPlan.items?.map((item) => (
                    <li key={item.id} className="font-medium">
                      {item.title}
                      {item.category_name && (
                        <span className="text-xs text-primary ml-2">({item.category_name})</span>
                      )}
                    </li>
                  ))}
                </ol>
                <p className="text-sm italic text-muted-foreground">
                  {t('kaizen.completeOne')}
                </p>
              </div>
              
              <Button
                className="w-full mt-4 rounded-full"
                onClick={confirmCommitment}
                data-testid="confirm-commitment-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {t('kaizen.confirmCommitment')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Close Day Modal */}
      <Dialog open={showCloseDay} onOpenChange={setShowCloseDay}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5" />
              {t('kaizen.closeDayTitle')}
            </DialogTitle>
            <DialogDescription>
              30 segundos para cerrar tu día
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('kaizen.reflection')}</Label>
              <Input
                placeholder={t('kaizen.reflectionPlaceholder')}
                value={closeDayForm.reflection_note}
                onChange={(e) => setCloseDayForm(prev => ({ ...prev, reflection_note: e.target.value }))}
                data-testid="reflection-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('kaizen.nextStep')}</Label>
              <Input
                placeholder={t('kaizen.nextStepPlaceholder')}
                value={closeDayForm.next_step_for_tomorrow}
                onChange={(e) => setCloseDayForm(prev => ({ ...prev, next_step_for_tomorrow: e.target.value }))}
                data-testid="next-step-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('kaizen.emotionalState')}</Label>
              <div className="flex gap-2 flex-wrap">
                {['bien', 'cansado', 'enfocado', 'disperso', 'motivado'].map(state => (
                  <Button
                    key={state}
                    variant={closeDayForm.emotional_state === state ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                    onClick={() => setCloseDayForm(prev => ({ ...prev, emotional_state: state }))}
                  >
                    {state}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button className="w-full rounded-full" onClick={handleCloseDay} data-testid="submit-close-day-btn">
              <Check className="w-4 h-4 mr-2" />
              {t('kaizen.closeDay')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Notification Settings Modal */}
      <Dialog open={showNotificationSettings} onOpenChange={setShowNotificationSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              {t('kaizen.notificationSettings')}
            </DialogTitle>
          </DialogHeader>
          
          {notificationSettings && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('kaizen.nightPlanningTime')}</Label>
                <Input
                  type="time"
                  value={notificationSettings.night_planning_time}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, night_planning_time: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kaizen.morningPlanningTime')}</Label>
                <Input
                  type="time"
                  value={notificationSettings.morning_planning_time}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, morning_planning_time: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kaizen.followupTime')}</Label>
                <Input
                  type="time"
                  value={notificationSettings.followup_time}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, followup_time: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kaizen.dayCloseTime')}</Label>
                <Input
                  type="time"
                  value={notificationSettings.day_close_time}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, day_close_time: e.target.value }))}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button className="w-full rounded-full" onClick={saveNotificationSettings}>
              <Check className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
